import React, { useRef, useEffect, useState, useMemo } from "react"; // Added useMemo
import { useGLTF, Sparkles, Environment, ContactShadows } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from 'three';

export function Model({ metalType = "gold" }) {
  const { nodes, materials } = useGLTF("/ring_gold_with_diamond.glb");
  const modelRef = useRef();
  const diamondRef = useRef();
  const [rotation, setRotation] = useState([Math.PI / 2, 0, Math.PI / 2]);
  const mouseDown = useRef(false);
  const lastMousePosition = useRef({ x: 0, y: 0 });
  const rotationSpeed = 0.01;
  
  const [scale, setScale] = useState([3, 3, 3]);
  
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      setScale(isMobile ? [4.5, 4.5, 4.5] : [3, 3, 3]);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const metalMaterials = useMemo(() => ({ // Memoize metalMaterials definition
    gold: {
      color: new THREE.Color("#FFD700").convertSRGBToLinear(),
      subsurfaceColor: new THREE.Color("#F5C85C").convertSRGBToLinear(),
      emissive: new THREE.Color("#3D2E08").multiplyScalar(0.07),
      metalness: 0.97,
      roughness: 0.07,
      reflectivity: 0.98,
      clearcoat: 0.3,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.6,
      anisotropy: 0.12,
      anisotropyRotation: Math.PI / 2,
    },
    silver: {
      color: new THREE.Color("#F0F0F8").convertSRGBToLinear(),
      subsurfaceColor: new THREE.Color("#D6D6E0").convertSRGBToLinear(),
      emissive: new THREE.Color("#393945").multiplyScalar(0.04),
      metalness: 0.92, 
      roughness: 0.05,
      reflectivity: 1.0,
      clearcoat: 0.22,
      clearcoatRoughness: 0.08,
      envMapIntensity: 1.7,
      anisotropy: 0.1,
      anisotropyRotation: 0,
    },
    roseGold: {
      color: new THREE.Color("#E0BFB8").convertSRGBToLinear(),
      subsurfaceColor: new THREE.Color("#C88C84").convertSRGBToLinear(),
      emissive: new THREE.Color("#4D2C28").multiplyScalar(0.06),
      metalness: 0.91,
      roughness: 0.09,
      reflectivity: 0.96,
      clearcoat: 0.25,
      clearcoatRoughness: 0.12,
      envMapIntensity: 1.4,
      anisotropy: 0.08,
      anisotropyRotation: Math.PI / 4,
    }
  }), []);


  const metalMaterial = useMemo(() => {
    const currentMetal = metalMaterials[metalType] || metalMaterials.gold;
    return new THREE.MeshPhysicalMaterial({
      color: currentMetal.color,
      emissive: currentMetal.emissive,
      metalness: currentMetal.metalness,
      roughness: currentMetal.roughness,
      reflectivity: currentMetal.reflectivity,
      clearcoat: currentMetal.clearcoat,
      clearcoatRoughness: currentMetal.clearcoatRoughness,
      envMapIntensity: currentMetal.envMapIntensity,
      anisotropy: currentMetal.anisotropy,
      anisotropyRotation: currentMetal.anisotropyRotation,
      transmission: 0.02,
      thickness: 0.5,
      attenuationDistance: 0.5,
      attenuationColor: currentMetal.subsurfaceColor,
      // REMOVED: needsUpdate: true
    });
  }, [metalType, metalMaterials]); // metalMaterials is now stable due to its own useMemo

  const diamondMaterial = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: new THREE.Color('#ffffff').convertSRGBToLinear(),
    metalness: 0.1,
    roughness: 0.01,
    transmission: 0.99,
    transparent: true,
    opacity: 0.9,
    envMapIntensity: 4.0,
    clearcoat: 1.0,
    clearcoatRoughness: 0.01,
    ior: 2.42,
    thickness: 3.5,
    attenuationDistance: 0.3,
    attenuationColor: new THREE.Color('#f0f8ff').convertSRGBToLinear(),
    reflectivity: 1.0,
    sheen: 1.0,
    sheenRoughness: 0.05,
    sheenColor: new THREE.Color('#ffffff').convertSRGBToLinear(),
    specularIntensity: 1.2,
    specularColor: new THREE.Color('#ffffff').convertSRGBToLinear(),
    // REMOVED: needsUpdate: true
  }), []);


  useFrame(({ clock }) => {
    // Auto-rotation for the ring
    if (!mouseDown.current && modelRef.current) {
      // Optimized state update to avoid unnecessary array recreation
      setRotation(prevRotation => [
        prevRotation[0] + 0.001,
        prevRotation[1] + 0.003,
        prevRotation[2] + 0.001
      ]);
    }
  });

  useEffect(() => {
    const handleMouseDown = (e) => {
      mouseDown.current = true;
      lastMousePosition.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseUp = () => { mouseDown.current = false; };
    const handleMouseMove = (e) => {
      if (mouseDown.current) {
        const deltaX = e.clientX - lastMousePosition.current.x;
        const deltaY = e.clientY - lastMousePosition.current.y;
        setRotation(prevRotation => [
          prevRotation[0] + deltaY * rotationSpeed,
          prevRotation[1] + deltaX * rotationSpeed,
          prevRotation[2]
        ]);
        lastMousePosition.current = { x: e.clientX, y: e.clientY };
      }
    };
    const handleTouchStart = (e) => {
      mouseDown.current = true;
      lastMousePosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const handleTouchMove = (e) => {
      if (mouseDown.current) {
        const deltaX = e.touches[0].clientX - lastMousePosition.current.x;
        const deltaY = e.touches[0].clientY - lastMousePosition.current.y;
        setRotation(prevRotation => [
          prevRotation[0] + deltaY * rotationSpeed,
          prevRotation[1] + deltaX * rotationSpeed,
          prevRotation[2]
        ]);
        lastMousePosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [rotationSpeed]); // rotationSpeed is constant, rotation was removed to prevent re-binding listeners on every rotation change

  return (
    <>
      <Environment preset="studio" />
      <ambientLight intensity={0.3} />
      <hemisphereLight 
        intensity={0.5} 
        color="#fffaf5" 
        groundColor="#1a1a1a" 
      />
      <spotLight 
        position={[5, 10, 7]} 
        angle={0.25} 
        penumbra={0.9} 
        intensity={1.0} 
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0001}
        color="#fff9e6"
      />
      <spotLight 
        position={[-5, 5, -7]} 
        angle={0.3} 
        penumbra={0.95} 
        intensity={0.8} 
        color="#f0f5ff"
      />
      <pointLight position={[3, -2, 0]} intensity={0.5} color="#fff6e0" />
      <pointLight position={[-3, 1, 2]} intensity={0.5} color="#f7faff" />
      <pointLight position={[0, 3, 5]} intensity={0.4} color="#ffffff" />

      <group
        ref={modelRef}
        dispose={null}
        position={[0, 0, 0]}
        scale={scale}
        rotation={rotation}
      >
        {nodes?.Cylinder_Material006_0 && (
          <mesh
            geometry={nodes.Cylinder_Material006_0.geometry}
            material={metalMaterial}
            castShadow
            receiveShadow
          />
        )}

        {nodes?.Gem_Material002_0 && (
          <group ref={diamondRef} position={[0, -1.25, 0]} rotation={[3*Math.PI/2, Math.PI, Math.PI]}>
            <mesh
              scale={[0.3, 0.3, 0.3]}
              geometry={nodes.Gem_Material002_0.geometry}
              material={diamondMaterial}
              castShadow
            >
              <Sparkles
                count={30}
                scale={0.5}
                size={3}
                speed={0.3}
                opacity={0.7}
                noise={0.1}
                color="#ffffff"
              />
              <Sparkles
                count={15}
                scale={0.3}
                size={2}
                speed={0.5}
                opacity={0.5}
                noise={0.2}
                color="#e0f7ff"
              />
            </mesh>
          </group>
        )}
      </group>
      
      <ContactShadows
        position={[0, -3.5, 0]}
        opacity={0.6}
        scale={20}
        blur={2}
        far={4.5}
      />
    </>
  );
}

useGLTF.preload("/ring_gold_with_diamond.glb");